'use client'

import { useEffect, useState } from 'react'
import { FileText } from 'lucide-react'
import { motion } from 'motion/react'

import { Tab, TabSelect } from '@/components/TabSelect'
import { Bill, Topic } from '@/payload-types'
import { useDashboard } from '@/providers/Dashboard'
import { useUserTracking } from '@/providers/UserTracking'
import { cn } from '@/utilities/ui'

import { RecommendedBills } from './components/RecommendedBills'
import { TrackedBills } from './components/TrackedBills'

export type TrackedBillsProps = {
  bills: Bill[]
  allBillTopics: Topic[]
}

const tabs: Tab[] = [
  {
    name: 'Tracked',
    value: 'tracked-bills',
  },
  {
    name: 'Recommended',
    value: 'recommended-bills',
  },
] as const

export const MyBills = ({ bills, allBillTopics = [] }: TrackedBillsProps) => {
  const { refreshUser } = useDashboard()
  const trackingContext = useUserTracking()
  const trackedBillIds = trackingContext?.trackedBillIds

  const [selectedTab, setSelectedTab] = useState<Tab['value']>(
    bills.length > 0 ? 'tracked-bills' : 'recommended-bills',
  )

  useEffect(() => {
    refreshUser()
  }, [trackedBillIds, refreshUser])

  return (
    <motion.div
      initial={false}
      animate={{ maxHeight: '60vh' }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className={cn(
        'flex w-full min-w-0 flex-col gap-8 rounded-2xl border border-border bg-background p-4 lg:p-6',
      )}
    >
      <div className="flex flex-col gap-2.5">
        <div className="flex flex-row items-center justify-between gap-1">
          <h2 className="flex items-center gap-2 font-body text-xl font-medium text-foreground">
            {' '}
            <FileText />
            My Bills
          </h2>
        </div>
        <TabSelect
          size="small"
          tabs={tabs}
          value={selectedTab}
          onTabChange={setSelectedTab}
          className="shrink-0"
          instanceKey="my-bills"
        />
      </div>

      <div role="tabpanel" className="flex flex-col gap-4 overflow-y-auto">
        {selectedTab === 'tracked-bills' && (
          <TrackedBills bills={bills} allBillTopics={allBillTopics} />
        )}
        {selectedTab === 'recommended-bills' && <RecommendedBills allBillTopics={allBillTopics} />}
      </div>
    </motion.div>
  )
}
